import { useState, useEffect } from 'react'
import { Modal, Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap'

function PromoForm({ show, onHide, onSubmit, promo, loading }) {
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    duration_days: 30,
    max_uses: 0,
    single_use: false,
    plan_codes: [],
    description: '',
  })
  const [error, setError] = useState('')

  useEffect(() => {
    if (promo) {
      setFormData({
        code: promo.code || '',
        discount_type: promo.discount_type || 'percentage',
        discount_value: promo.discount_value || 10,
        duration_days: promo.duration_days || 30,
        max_uses: promo.max_uses || 0,
        single_use: promo.single_use || false,
        plan_codes: promo.plan_codes || [],
        description: promo.description || '',
      })
    } else {
      setFormData({
        code: '',
        discount_type: 'percentage',
        discount_value: 10,
        duration_days: 30,
        max_uses: 0,
        single_use: false,
        plan_codes: [],
        description: '',
      })
    }
    setError('')
  }, [promo, show])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handlePlanCodeChange = (planCode) => {
    setFormData(prev => ({
      ...prev,
      plan_codes: prev.plan_codes.includes(planCode)
        ? prev.plan_codes.filter(p => p !== planCode)
        : [...prev.plan_codes, planCode],
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    if (!formData.code.trim()) {
      setError('Le code promo est obligatoire')
      return
    }

    if (formData.discount_value <= 0) {
      setError('La valeur de réduction doit être positive')
      return
    }

    if (formData.discount_type === 'percentage' && formData.discount_value > 100) {
      setError('Le pourcentage ne peut pas dépasser 100%')
      return
    }

    onSubmit({
      ...formData,
      discount_value: Number(formData.discount_value),
      duration_days: Number(formData.duration_days),
      max_uses: Number(formData.max_uses),
    })
  }

  const planOptions = ['starter', 'basic', 'pro', 'enterprise']

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{promo ? 'Modifier le code promo' : 'Créer un code promo'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Code promo *</Form.Label>
                <Form.Control
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  placeholder="PROMO2026"
                  disabled={!!promo}
                />
                <Form.Text className="text-muted">
                  Le code que les utilisateurs entreront
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Description interne du code"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Type de réduction</Form.Label>
                <Form.Select
                  name="discount_type"
                  value={formData.discount_type}
                  onChange={handleChange}
                >
                  <option value="percentage">Pourcentage (%)</option>
                  <option value="fixed">Montant fixe (€)</option>
                  <option value="trial_days">Jours d'essai gratuits</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Valeur de la réduction</Form.Label>
                <Form.Control
                  type="number"
                  name="discount_value"
                  value={formData.discount_value}
                  onChange={handleChange}
                  min="1"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Durée (jours)</Form.Label>
                <Form.Control
                  type="number"
                  name="duration_days"
                  value={formData.duration_days}
                  onChange={handleChange}
                  min="0"
                />
                <Form.Text className="text-muted">
                  0 = durée illimitée
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Utilisations maximum</Form.Label>
                <Form.Control
                  type="number"
                  name="max_uses"
                  value={formData.max_uses}
                  onChange={handleChange}
                  min="0"
                />
                <Form.Text className="text-muted">
                  0 = illimité
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6} className="d-flex align-items-end">
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  name="single_use"
                  label="Usage unique par utilisateur"
                  checked={formData.single_use}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Plans applicables</Form.Label>
            <div className="d-flex flex-wrap gap-3">
              {planOptions.map(plan => (
                <Form.Check
                  key={plan}
                  type="checkbox"
                  label={plan.charAt(0).toUpperCase() + plan.slice(1)}
                  checked={formData.plan_codes.includes(plan)}
                  onChange={() => handlePlanCodeChange(plan)}
                />
              ))}
            </div>
            <Form.Text className="text-muted">
              Laisser vide pour tous les plans
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Annuler
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Enregistrement...
              </>
            ) : promo ? 'Mettre à jour' : 'Créer'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default PromoForm
